import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { showSuccess, showError } from '../utils/toast';
import {
    FaArrowLeft,
    FaPlus,
    FaTrash,
    FaStar,
    FaCalendar,
    FaToggleOn,
    FaToggleOff
} from 'react-icons/fa';

const ChangelogManagement = () => {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [changelogs, setChangelogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        version: '',
        title: '',
        description: ''
    });

    useEffect(() => {
        if (authLoading) return;
        if (!user || user.role !== 'admin') {
            navigate('/');
            return;
        }
        fetchChangelogs();
    }, [user, authLoading, navigate]);

    const fetchChangelogs = async () => {
        try {
            const { data } = await axios.get('/api/changelog');
            setChangelogs(data);
        } catch (error) {
            console.error('Changelog listesi hatası:', error);
            showError('Güncellemeler yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.version || !formData.title || !formData.description) {
            showError('Tüm alanları doldurun');
            return;
        }

        setSubmitting(true);
        try {
            await axios.post('/api/changelog', formData);
            showSuccess('Güncelleme eklendi!');
            setFormData({ version: '', title: '', description: '' });
            fetchChangelogs();
        } catch (error) {
            showError(error.response?.data?.message || 'Ekleme başarısız');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu güncellemeyi silmek istediğinize emin misiniz?')) return;

        try {
            await axios.delete(`/api/changelog/${id}`);
            showSuccess('Güncelleme silindi');
            setChangelogs(changelogs.filter(c => c._id !== id));
        } catch (error) {
            showError('Silme başarısız');
        }
    };

    const toggleActive = async (changelog) => {
        try {
            await axios.put(`/api/changelog/${changelog._id}`, {
                isActive: !changelog.isActive
            });
            setChangelogs(changelogs.map(c =>
                c._id === changelog._id ? { ...c, isActive: !c.isActive } : c
            ));
            showSuccess(`Güncelleme ${!changelog.isActive ? 'aktif' : 'pasif'} yapıldı`);
        } catch (error) {
            showError('Durum güncellenemedi');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8 transition-colors">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/admin/dashboard')}
                        className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-4 transition-colors"
                    >
                        <FaArrowLeft className="mr-2" />
                        Admin Paneline Dön
                    </button>
                    <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                        Yenilikler Yönetimi
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Kullanıcılara gösterilecek güncelleme duyurularını yönetin
                    </p>
                </div>

                {/* Add New Form */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 mb-8 border border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                        <FaPlus className="mr-2 text-green-500" />
                        Yeni Güncelleme Ekle
                    </h2>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Versiyon
                                </label>
                                <input
                                    type="text"
                                    value={formData.version}
                                    onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                                    placeholder="örn: v1.5.0 veya Ocak 2026"
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Başlık
                                </label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="örn: Yeni Arayüz Yayında!"
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Açıklama
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                rows={4}
                                placeholder="Güncelleme detaylarını yazın..."
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
                        >
                            {submitting ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            ) : (
                                <>
                                    <FaStar />
                                    <span>Güncelleme Ekle</span>
                                </>
                            )}
                        </button>
                    </form>
                </div>

                {/* Changelog List */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                            Mevcut Güncellemeler ({changelogs.length})
                        </h2>
                    </div>

                    {changelogs.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                            Henüz güncelleme eklenmemiş
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                            {changelogs.map((changelog) => (
                                <div key={changelog._id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${changelog.isActive
                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                                    }`}>
                                                    {changelog.version}
                                                </span>
                                                <span className={`text-xs ${changelog.isActive ? 'text-green-600' : 'text-gray-500'}`}>
                                                    {changelog.isActive ? 'Aktif' : 'Pasif'}
                                                </span>
                                            </div>
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {changelog.title}
                                            </h3>
                                            <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm line-clamp-2">
                                                {changelog.description}
                                            </p>
                                            <div className="flex items-center mt-2 text-xs text-gray-500">
                                                <FaCalendar className="mr-1" />
                                                {formatDate(changelog.createdAt)}
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-2 ml-4">
                                            <button
                                                onClick={() => toggleActive(changelog)}
                                                className={`p-2 rounded-lg transition-colors ${changelog.isActive
                                                    ? 'text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30'
                                                    : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                    }`}
                                                title={changelog.isActive ? 'Pasif yap' : 'Aktif yap'}
                                            >
                                                {changelog.isActive ? <FaToggleOn className="text-2xl" /> : <FaToggleOff className="text-2xl" />}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(changelog._id)}
                                                className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                                title="Sil"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChangelogManagement;
